import React from 'react';
import { THEME_PRESETS } from '../../../lib/themes';

const ThemeSection = ({ formData, onChange }) => {
  const currentTheme = formData?.theme || 'default';

  const handleThemeChange = (themeId) => {
    onChange({
      target: {
        name: 'theme',
        value: themeId
      }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">
          Цветовая тема
        </h3>
        <p className="text-sm text-text-secondary mb-6">
          Выберите цветовую схему для вашего сайта. Тема влияет на все элементы: кнопки, фон, текст и акценты.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(THEME_PRESETS).map(([themeId, theme]) => {
          const isSelected = currentTheme === themeId;
          const { colors, radius, typography } = theme;

          // Получаем короткое название шрифта
          const fontName = typography.fontFamily.split(',')[0].replace(/"/g, '').trim();

          return (
            <button
              key={themeId}
              type="button"
              onClick={() => handleThemeChange(themeId)}
              className={`
                relative p-4 rounded-xl border-2 transition-all text-left
                ${isSelected
                  ? 'border-primary ring-2 ring-primary/20'
                  : 'border-border hover:border-primary/50'
                }
              `}
            >
              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-primary-foreground"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Theme preview */}
              <div
                className="w-full h-24 mb-3 overflow-hidden border"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                  borderRadius: radius.lg
                }}
              >
                {/* Preview content */}
                <div className="h-full p-3 flex flex-col justify-between">
                  {/* Header bar */}
                  <div className="flex items-center gap-2">
                    <div
                      style={{ backgroundColor: colors.primary, borderRadius: radius.sm }}
                      className="w-6 h-6"
                    />
                    <div
                      style={{ backgroundColor: colors.foreground, opacity: 0.7, borderRadius: radius.sm }}
                      className="h-2 w-16"
                    />
                  </div>

                  {/* Content preview */}
                  <div className="space-y-1.5">
                    <div
                      style={{ backgroundColor: colors.foreground, opacity: 0.8, borderRadius: radius.sm }}
                      className="h-2 w-full"
                    />
                    <div
                      style={{ backgroundColor: colors.textSecondary, opacity: 0.6, borderRadius: radius.sm }}
                      className="h-2 w-3/4"
                    />
                  </div>

                  {/* Button preview */}
                  <div className="flex gap-2">
                    <div
                      style={{ backgroundColor: colors.primary, borderRadius: radius.md }}
                      className="h-5 w-14"
                    />
                    <div
                      style={{
                        backgroundColor: colors.muted,
                        border: `1px solid ${colors.border}`,
                        borderRadius: radius.md
                      }}
                      className="h-5 w-14"
                    />
                  </div>
                </div>
              </div>

              {/* Theme name */}
              <div className="font-medium text-foreground">{theme.name}</div>

              {/* Theme details */}
              <div className="flex items-center gap-3 mt-2">
                {/* Color palette */}
                <div className="flex gap-1">
                  <div
                    className="w-4 h-4 rounded-full border border-border"
                    style={{ backgroundColor: colors.primary }}
                    title="Primary"
                  />
                  <div
                    className="w-4 h-4 rounded-full border border-border"
                    style={{ backgroundColor: colors.background }}
                    title="Background"
                  />
                  <div
                    className="w-4 h-4 rounded-full border border-border"
                    style={{ backgroundColor: colors.foreground }}
                    title="Foreground"
                  />
                </div>
                {/* Font name */}
                <span className="text-xs text-text-secondary truncate" title={fontName}>
                  {fontName}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Current theme info */}
      {(() => {
        const theme = THEME_PRESETS[currentTheme];
        const fontName = theme?.typography.fontFamily.split(',')[0].replace(/"/g, '').trim();
        const headingFontName = theme?.typography.fontFamilyHeading.split(',')[0].replace(/"/g, '').trim();
        return (
          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 flex-shrink-0"
                style={{
                  backgroundColor: theme?.colors.primary,
                  borderRadius: theme?.radius.lg
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground">
                  Выбрана тема: {theme?.name}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-text-secondary">
                  <span>Шрифт: {fontName}</span>
                  {fontName !== headingFontName && <span>Заголовки: {headingFontName}</span>}
                  <span>Скругление: {theme?.radius.md}</span>
                </div>
                <p className="text-sm text-text-secondary mt-2">
                  Изменения применятся после сохранения настроек.
                </p>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ThemeSection;
